import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Info, ShieldAlert, Sparkles, Target, MessagesSquare, Check, Building2, X, Ban, Mail, Send } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Settings() {
  const [keywords, setKeywords] = useState([]);
  const [input, setInput] = useState("");
  const [config, setConfig] = useState(null);
  const [matchMode, setMatchMode] = useState("name_only");
  const [brandContext, setBrandContext] = useState("");
  const [industry, setIndustry] = useState("");
  const [ownDomains, setOwnDomains] = useState("");
  const [watchlist, setWatchlist] = useState("");
  const [savingWatchlist, setSavingWatchlist] = useState(false);
  const [savingCtx, setSavingCtx] = useState(false);
  const [forbiddenWords, setForbiddenWords] = useState([]);
  const [newForbidden, setNewForbidden] = useState("");
  const [savingForbidden, setSavingForbidden] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [digestRecipients, setDigestRecipients] = useState("");
  const [emailSender, setEmailSender] = useState("");
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);

  const load = async () => {
    const [k, c, s, e] = await Promise.all([
      api.get("/keywords"), api.get("/config"), api.get("/settings"), api.get("/email/status"),
    ]);
    setKeywords(k.data);
    setConfig(c.data);
    setMatchMode(s.data.match_mode);
    setBrandContext(s.data.brand_context || "");
    setIndustry(s.data.industry || "");
    setOwnDomains((s.data.own_domains || []).join("\n"));
    setWatchlist((s.data.watchlist_domains || []).join("\n"));
    setForbiddenWords(s.data.forbidden_words || []);
    setRecipients((s.data.email_recipients || []).join("\n"));
    setDigestRecipients((s.data.digest_recipients || []).join("\n"));
    setEmailSender(e.data?.sender || "");
  };

  useEffect(() => {
    load();
  }, []);

  const saveContext = async () => {
    setSavingCtx(true);
    try {
      const res = await api.put("/settings", {
        brand_context: brandContext,
        industry: industry,
        own_domains: ownDomains.split("\n").map((d) => d.trim()).filter(Boolean),
      });
      const removed = res.data?.removed_own_cases || 0;
      if (removed > 0) {
        toast.success(`Gespeichert. ${removed} eigene Seite(n) aus der Fallakte entfernt.`);
      } else {
        toast.success("Marken-Kontext gespeichert.");
      }
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingCtx(false);
    }
  };

  const saveWatchlist = async () => {
    setSavingWatchlist(true);
    try {
      const list = watchlist.split("\n").map((d) => d.trim()).filter(Boolean);
      const res = await api.put("/settings", { watchlist_domains: list });
      setWatchlist((res.data?.watchlist_domains || []).join("\n"));
      toast.success(`${(res.data?.watchlist_domains || []).length} Watchlist-Domains gespeichert.`);
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingWatchlist(false);
    }
  };

  const saveRecipients = async () => {
    setSavingRecipients(true);
    try {
      const list = recipients.split("\n").map((r) => r.trim()).filter(Boolean);
      const digest = digestRecipients.split("\n").map((r) => r.trim()).filter(Boolean);
      const res = await api.put("/settings", { email_recipients: list, digest_recipients: digest });
      setRecipients((res.data?.email_recipients || []).join("\n"));
      setDigestRecipients((res.data?.digest_recipients || []).join("\n"));
      toast.success("E-Mail-Einstellungen gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingRecipients(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const res = await api.post("/email/test");
      toast.success(`Test-E-Mail an ${(res.data?.recipients || []).length} Empfänger gesendet.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Versand fehlgeschlagen.");
    } finally {
      setSendingTest(false);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const res = await api.post("/email/digest");
      toast.success(`Tagesliste mit ${res.data?.count ?? 0} Treffer(n) gesendet.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Versand fehlgeschlagen.");
    } finally {
      setSendingDigest(false);
    }
  };

  const sendWeeklyNow = async () => {
    setSendingWeekly(true);
    try {
      const res = await api.post("/email/weekly");
      toast.success(
        `Wochenübersicht gesendet: ${res.data?.violations ?? 0} Verstöße, ${res.data?.mentions ?? 0} Erwähnungen.`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Versand fehlgeschlagen.");
    } finally {
      setSendingWeekly(false);
    }
  };

  const changeMode = async (mode) => {
    setMatchMode(mode);
    try {
      await api.put("/settings", { match_mode: mode });
      toast.success("Erkennungsmodus gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    }
  };

  const add = async () => {
    const term = input.trim();
    if (!term) return;
    await api.post("/keywords", { term });
    setInput("");
    toast.success("Begriff gespeichert.");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/keywords/${id}`);
    load();
  };

  const saveForbidden = async (list) => {
    setSavingForbidden(true);
    try {
      await api.put("/settings", { forbidden_words: list });
      setForbiddenWords(list);
      toast.success("Sperrliste gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingForbidden(false);
    }
  };

  const addForbidden = () => {
    const w = newForbidden.trim();
    if (!w) return;
    if (forbiddenWords.some((x) => x.toLowerCase() === w.toLowerCase())) {
      toast.info("Begriff ist bereits in der Liste.");
      setNewForbidden("");
      return;
    }
    saveForbidden([...forbiddenWords, w]);
    setNewForbidden("");
  };

  const removeForbidden = (word) => {
    saveForbidden(forbiddenWords.filter((x) => x !== word));
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <PageHeader title="Einstellungen" subtitle="Erkennungsmodus, geschützte Namen und Systeminfo" />

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A]">
            <Target className="h-4 w-4 text-white" />
          </span>
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Erkennungsmodus</h3>
        </div>
        <p className="mt-1 text-sm text-[#6B6A6E]">
          Steuert, wann ein Treffer als Verstoß erfasst wird.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <button
            data-testid="mode-name-only"
            onClick={() => changeMode("name_only")}
            className={`relative rounded-md border p-5 text-left transition-all ${
              matchMode === "name_only"
                ? "border-[#0A0A0A] bg-[#FAF7F2] ring-1 ring-[#0A0A0A]"
                : "border-[#E8E4DF] hover:bg-[#FAF7F2]"
            }`}
          >
            {matchMode === "name_only" && (
              <span className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-[#0A0A0A] text-white">
                <Check className="h-3.5 w-3.5" />
              </span>
            )}
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#0A0A0A]">
              <Target className="h-5 w-5 text-white" />
            </div>
            <p className="mt-3 font-heading text-base font-bold text-[#0A0A0A]">Nur Namensanmaßung</p>
            <p className="mt-1 text-sm text-[#6B6A6E]">
              Erfasst nur Profile, Benutzernamen (@handle) oder Seiten, die den geschützten Namen
              <b> selbst führen</b> und damit werben. Empfohlen.
            </p>
          </button>

          <button
            data-testid="mode-all-mentions"
            onClick={() => changeMode("all_mentions")}
            className={`relative rounded-md border p-5 text-left transition-all ${
              matchMode === "all_mentions"
                ? "border-[#0A0A0A] bg-[#FAF7F2] ring-1 ring-[#0A0A0A]"
                : "border-[#E8E4DF] hover:bg-[#FAF7F2]"
            }`}
          >
            {matchMode === "all_mentions" && (
              <span className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-[#0A0A0A] text-white">
                <Check className="h-3.5 w-3.5" />
              </span>
            )}
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#A6A4A9]">
              <MessagesSquare className="h-5 w-5 text-white" />
            </div>
            <p className="mt-3 font-heading text-base font-bold text-[#0A0A0A]">Alle Erwähnungen</p>
            <p className="mt-1 text-sm text-[#6B6A6E]">
              Erfasst zusätzlich Erwähnungen des Namens in <b>Beiträgen, Reels und Gruppen</b> –
              mehr Treffer, aber auch mehr Rauschen.
            </p>
          </button>
        </div>
      </Card>

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A]">
            <Building2 className="h-4 w-4 text-white" />
          </span>
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Marken-Kontext für die KI</h3>
        </div>
        <p className="mt-1 text-sm text-[#6B6A6E]">
          Beschreiben Sie Ihr Unternehmen / Ihre Gruppe (Branche, Produkte, offizielle Website). So kann die
          KI <b>fremde Firmen mit zufällig ähnlichem Namen</b> ausschließen und liefert deutlich weniger Fehltreffer.
        </p>

        <div className="mt-4 space-y-1.5">
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Beschreibung der Gruppe</label>
          <Textarea
            data-testid="settings-brand-context"
            value={brandContext}
            onChange={(e) => setBrandContext(e.target.value)}
            placeholder="z. B. STIG ROCK / STIG International / Diamond Solution – Unternehmensgruppe im Bereich … Produkte: … Offizielle Website: stigrock.com. Vertriebspartner bewerben unsere Produkte mit unserem Namen."
            className="min-h-[120px] rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
          />
        </div>

        <div className="mt-4 space-y-1.5">
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Branche</label>
          <Input
            data-testid="settings-industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="z. B. Diamanten / Edelsteine, Marketing & Vertrieb, Lifestyle-Produkte"
            className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
          />
          <p className="text-xs text-[#8B8A8E]">
            Die KI schließt so Profile/Artikel aus einer <b>offensichtlich anderen Branche</b> (nur zufällige
            Namensgleichheit) aus – das senkt Fehltreffer deutlich.
          </p>
        </div>

        <div className="mt-4 space-y-1.5">
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Eigene Seiten, Profile & Kanäle (eine pro Zeile)</label>
          <Textarea
            data-testid="settings-own-domains"
            value={ownDomains}
            onChange={(e) => setOwnDomains(e.target.value)}
            placeholder={"stigrock.com\ninstagram.com/stigrock_official\nvimeo.com/stiggroup\n@stigrock_official\nstigrock"}
            className="min-h-[110px] rounded-md border-[#E0DCD6] bg-[#FAF7F2] font-mono text-xs focus-visible:ring-[#0A0A0A]"
          />
          <p className="text-xs text-[#8B8A8E]">
            Diese werden NIE als Verstoß erfasst (Ihre eigenen Seiten). Erlaubt sind: <b>Website-Domains</b> (stigrock.com),
            <b> vollständige Profil-URLs</b> (instagram.com/stigrock_official, vimeo.com/…) und <b>reine Benutzernamen/Handles</b>
            (@stigrock_official) – ein Handle schließt diesen Namen auf allen Plattformen aus.
          </p>
        </div>

        <Button
          data-testid="settings-save-context"
          onClick={saveContext}
          disabled={savingCtx}
          className="mt-4 rounded-md bg-[#0A0A0A] px-5 text-white hover:bg-[#262626]"
        >
          {savingCtx ? "Speichert …" : "Kontext speichern"}
        </Button>
      </Card>

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A]">
            <ShieldAlert className="h-4 w-4 text-white" />
          </span>
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Direkte Quellen-Watchlist</h3>
        </div>
        <p className="mt-2 text-sm text-[#6B6A6E]">
          Hochrisiko-Seiten (Scam-/Warn-/Bewertungsportale, Aufsichtsbehörden), die bei jedem Scan
          <b> direkt</b> nach euren Marken durchsucht werden – <b>unabhängig vom Google-Index</b>, damit auch
          brandneue oder nicht indexierte Seiten gefunden werden. Eine Domain pro Zeile.
        </p>
        <Textarea
          data-testid="settings-watchlist"
          value={watchlist}
          onChange={(e) => setWatchlist(e.target.value)}
          placeholder={"scamsradar.com\nbehindmlm.com\nbafin.de\nfma.gv.at\nwatchlist-internet.at"}
          className="mt-4 min-h-[150px] rounded-md border-[#E0DCD6] bg-[#FAF7F2] font-mono text-xs focus-visible:ring-[#0A0A0A]"
        />
        <Button
          data-testid="settings-save-watchlist"
          onClick={saveWatchlist}
          disabled={savingWatchlist}
          className="mt-4 rounded-md bg-[#0A0A0A] px-5 text-white hover:bg-[#262626]"
        >
          <Check className="mr-2 h-4 w-4" /> {savingWatchlist ? "Speichert …" : "Watchlist speichern"}
        </Button>
      </Card>

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A]">
            <Ban className="h-4 w-4 text-white" />
          </span>
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Sperrliste / No-Go-Wörter</h3>
        </div>
        <p className="mt-1 text-sm text-[#6B6A6E]">
          Verwendet ein Profil den geschützten Markennamen <b>gemeinsam</b> mit einem dieser Begriffe,
          wird das als <b>schwerer Compliance-Verstoß</b> gewertet (z. B. Diamanten als „Investment",
          „Rendite" oder „Geldanlage"). Aktuell <b>{forbiddenWords.length}</b> Begriffe.
        </p>

        <div className="mt-4 flex gap-2">
          <Input
            data-testid="settings-forbidden-input"
            value={newForbidden}
            onChange={(e) => setNewForbidden(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addForbidden()}
            placeholder="Neues No-Go-Wort hinzufügen …"
            className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
          />
          <Button
            data-testid="settings-add-forbidden"
            onClick={addForbidden}
            disabled={savingForbidden}
            className="rounded-md bg-[#0A0A0A] px-5 text-white hover:bg-[#262626]"
          >
            <Plus className="mr-2 h-4 w-4" /> Hinzufügen
          </Button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {forbiddenWords.length === 0 && <p className="text-sm text-[#8B8A8E]">Noch keine No-Go-Wörter.</p>}
          {forbiddenWords.map((w) => (
            <span
              key={w}
              data-testid={`forbidden-word-${w}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#EDE9E3] bg-[#FAF7F2] px-3 py-1.5 text-sm font-medium text-[#0A0A0A]"
            >
              {w}
              <button
                data-testid={`remove-forbidden-${w}`}
                onClick={() => removeForbidden(w)}
                className="text-[#A6A4A9] transition-colors hover:text-rose-600"
                aria-label={`${w} entfernen`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      </Card>

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8">
        <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Geschützte Namen / Marken</h3>
        <p className="mt-1 text-sm text-[#6B6A6E]">
          Diese Begriffe stehen im Scanner als Vorschlag bereit.
        </p>

        <div className="mt-4 flex gap-2">
          <Input
            data-testid="settings-keyword-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Neuen geschützten Namen hinzufügen …"
            className="rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
          />
          <Button
            data-testid="settings-add-keyword"
            onClick={add}
            className="rounded-md bg-[#0A0A0A] px-5 text-white hover:bg-[#262626]"
          >
            <Plus className="mr-2 h-4 w-4" /> Hinzufügen
          </Button>
        </div>

        <div className="mt-5 space-y-2">
          {keywords.length === 0 && <p className="text-sm text-[#8B8A8E]">Noch keine Begriffe gespeichert.</p>}
          {keywords.map((k) => (
            <div
              key={k.id}
              data-testid={`settings-keyword-${k.id}`}
              className="flex items-center justify-between rounded-md border border-[#EDE9E3] bg-white px-4 py-3 transition-colors hover:bg-[#FAF7F2]"
            >
              <span className="text-sm font-semibold text-[#0A0A0A]">{k.term}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-md text-[#A6A4A9] hover:bg-rose-50 hover:text-rose-600"
                onClick={() => remove(k.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A]">
            <Mail className="h-4 w-4 text-white" />
          </span>
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">E-Mail-Benachrichtigungen</h3>
        </div>
        <p className="mt-2 text-sm text-[#6B6A6E]">
          Empfänger, die bei jedem neuen Verstoß automatisch per E-Mail benachrichtigt werden – eine
          Adresse pro Zeile.
        </p>
        <Textarea
          data-testid="settings-email-recipients"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          placeholder={"name@firma.de\nalerts@firma.de"}
          className="mt-4 min-h-[110px] rounded-md border-[#E0DCD6] bg-[#FAF7F2] font-mono text-sm focus-visible:ring-[#0A0A0A]"
        />
        {emailSender && (
          <p className="mt-2 text-xs text-[#8B8A8E]">
            Absender: <b>{emailSender}</b>
          </p>
        )}

        <div className="mt-6 border-t border-[#F0ECE6] pt-5">
          <h4 className="text-sm font-bold text-[#0A0A0A]">Tägliche Treffer-Liste (separate Adresse)</h4>
          <p className="mt-1 text-sm text-[#6B6A6E]">
            Zusätzlich zu den Einzel-Mails geht jeden Morgen um 08:00 Uhr eine Sammel-Liste mit allen
            neuen Treffern des Tages (inkl. Links) an diese Adresse(n) – eine pro Zeile. Um 08:10 Uhr folgt
            zusätzlich die <b>7-Tage-Übersicht</b> mit allen Verstößen und neutralen Medien-Erwähnungen der letzten Woche.
          </p>
          <Textarea
            data-testid="settings-digest-recipients"
            value={digestRecipients}
            onChange={(e) => setDigestRecipients(e.target.value)}
            placeholder={"legal@firma.de"}
            className="mt-3 min-h-[80px] rounded-md border-[#E0DCD6] bg-[#FAF7F2] font-mono text-sm focus-visible:ring-[#0A0A0A]"
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            data-testid="settings-save-recipients"
            onClick={saveRecipients}
            disabled={savingRecipients}
            className="rounded-md bg-[#0A0A0A] px-5 text-white hover:bg-[#262626]"
          >
            <Check className="mr-2 h-4 w-4" /> {savingRecipients ? "Speichert …" : "E-Mail-Einstellungen speichern"}
          </Button>
          <Button
            data-testid="settings-send-test-email"
            onClick={sendTestEmail}
            disabled={sendingTest}
            variant="outline"
            className="rounded-md border-[#E0DCD6] px-5 text-[#0A0A0A] hover:bg-[#FAF7F2]"
          >
            <Send className="mr-2 h-4 w-4" /> {sendingTest ? "Sendet …" : "Test-E-Mail senden"}
          </Button>
          <Button
            data-testid="settings-send-digest"
            onClick={sendDigestNow}
            disabled={sendingDigest}
            variant="outline"
            className="rounded-md border-[#E0DCD6] px-5 text-[#0A0A0A] hover:bg-[#FAF7F2]"
          >
            <Send className="mr-2 h-4 w-4" /> {sendingDigest ? "Sendet …" : "Tagesliste jetzt senden"}
          </Button>
          <Button
            data-testid="settings-send-weekly"
            onClick={sendWeeklyNow}
            disabled={sendingWeekly}
            variant="outline"
            className="rounded-md border-[#E0DCD6] px-5 text-[#0A0A0A] hover:bg-[#FAF7F2]"
          >
            <Send className="mr-2 h-4 w-4" /> {sendingWeekly ? "Sendet …" : "7-Tage-Übersicht jetzt senden"}
          </Button>
        </div>
      </Card>

      <Card className="rounded-lg border-[#E8E4DF] bg-white p-6 shadow-[0_2px_16px_rgb(10,10,10,0.03)] md:p-8">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A]">
            <Info className="h-4 w-4 text-white" />
          </span>
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A]">Suchmodus</h3>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#0A0A0A] px-3.5 py-1.5 text-sm font-semibold text-white">
            {config?.search_mode === "serper" ? "Serper.dev (Live)" : "Demo-Modus"}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-sm border border-[#E8E4DF] bg-[#F4F1EC] px-3.5 py-1.5 text-sm font-semibold text-[#0A0A0A]">
            <Sparkles className="h-3.5 w-3.5" /> KI: Gemini 3 Flash
          </span>
        </div>
        {config?.search_mode !== "serper" && (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-amber-50 p-4 text-sm text-amber-800">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Aktuell läuft die Profilsuche im <b>Demo-Modus</b> (simulierte Treffer). Die KI-Bewertung
              ist real. Für echte Live-Suche kann ein Scraping-Dienst (z. B. Bright Data, Apify) oder
              Serper.dev/SerpAPI angebunden werden.
            </span>
          </div>
        )}
      </Card>
    </div>
  );
}
